import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import type { GatewaySnapshot } from './context';

type Setup = { id: string; status: string; setup_code?: string; qr?: string; expires_at: string };

export default function MobileDevices({ apiClient, state, ready, refresh }: {
  apiClient: ApiClient; state: GatewaySnapshot['mobile']; ready: boolean; refresh: () => Promise<void>;
}) {
  const [label, setLabel] = useState('My phone');
  const [days, setDays] = useState(7);
  const [setup, setSetup] = useState<Setup | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const request = useRef<string | null>(null);
  const previous = useRef(new Set<string>());
  useEffect(() => {
    if (!setup) return;
    const timer = setTimeout(() => setSetup(null), Math.max(0, Date.parse(setup.expires_at) - Date.now()));
    return () => clearTimeout(timer);
  }, [setup]);
  useEffect(() => {
    if (setup && state?.devices.some(d => d.active && !previous.current.has(d.id))) {
      const timer = setTimeout(() => { setSetup(null); setMessage('Phone connected. You can revoke its access below.'); }, 0);
      return () => clearTimeout(timer);
    }
  }, [setup, state]);
  async function pair() {
    setBusy(true); setMessage(null); setSetup(null);
    previous.current = new Set(state?.devices.map(d => d.id));
    request.current ??= crypto.randomUUID();
    try {
      const ticket = await apiClient.post<{ ticket: string; origin: string }>('/api/assistant/gateway/ticket', {});
      const origin = new URL(ticket.origin);
      if (origin.origin !== ticket.origin || origin.protocol !== 'https:' || origin.username || origin.password) throw Error();
      const exchange = await fetch(`${origin.origin}/session`, { method: 'POST', credentials: 'include', redirect: 'error',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams({ ticket: ticket.ticket }) });
      if (!exchange.ok) throw Error();
      const result = await fetch(`${origin.origin}/mobile/setup`, { method: 'POST', credentials: 'include', redirect: 'error',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: request.current, label, days }) });
      if (!result.ok) throw Error();
      const issued: Setup = await result.json();
      if (issued.status !== 'ready' || !issued.setup_code || !Number.isFinite(Date.parse(issued.expires_at))) throw Error();
      setSetup(issued); request.current = null;
    } catch {
      // A lost response is never retried with a fresh issuance identity.
      setMessage('Pairing could not be confirmed. Dismiss this attempt before creating another code.');
    } finally { setBusy(false); }
  }
  async function revoke(id: string) {
    setBusy(true); setMessage(null);
    try { await apiClient.post(`/api/assistant/gateway/mobile/devices/${id}/revoke`, {}); await refresh(); }
    catch { setMessage('Revocation could not be confirmed. Check again before relying on it.'); }
    finally { setBusy(false); }
  }
  async function dismiss() {
    const id = setup?.id ?? request.current;
    if (!id) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/assistant/gateway/mobile/setups/${id}/revoke`, {});
      setSetup(null); request.current = null; setMessage(null);
    } catch { setMessage('The pairing code could not be revoked. Wait for it to expire before creating another.'); }
    finally { setBusy(false); }
  }
  return <section className="personal-gateway-card assistant-mobile-devices" aria-labelledby="assistant-mobile-title">
    <h2 id="assistant-mobile-title">OpenClaw on your phone</h2>
    <p>Connect the OpenClaw Android or iPhone app to this assistant. Your chat stays with you. Review app and executor access in Mission Control.</p>
    <p>Already paired? Reconnect in the app. This preview requires a fresh device identity after access expires or is revoked.</p>
    {state?.supported && ready ? <>
      <label>Device name<input value={label} maxLength={120} disabled={busy || !!setup} onChange={e => setLabel(e.target.value)} autoComplete="off" /></label>
      <label>Access expires after<select value={days} disabled={busy || !!setup} onChange={e => setDays(Number(e.target.value))}>
        {[1, 7, 14, 30].map(n => <option key={n} value={n}>{n} {n === 1 ? 'day' : 'days'}</option>)}
      </select></label>
      {!setup && !request.current && <button disabled={busy || !label.trim()} onClick={() => { void pair(); }}>{busy ? 'Preparing…' : 'Create pairing code'}</button>}
      {setup && <div className="assistant-mobile-code">
        <p>Scan this code in the OpenClaw app, or paste the setup code. Keep it private. It expires in ten minutes and connects one phone.</p>
        {setup.qr?.startsWith('data:image/png;base64,') && <img src={setup.qr} alt="Private, one-use OpenClaw pairing code" width={240} height={240} />}
        <label>Setup code<textarea readOnly value={setup.setup_code} rows={3} autoComplete="off" spellCheck={false} /></label>
        <p>Access ends {new Date(Date.now() + days * 86400000).toLocaleDateString()}. Phone commands and administrative settings are unavailable.</p>
      </div>}
      {(setup || request.current) && <button className="secondary" disabled={busy} onClick={() => { void dismiss(); }}>Dismiss pairing code</button>}
    </> : <p>Mobile pairing becomes available when your assistant’s mobile adapter is ready.</p>}
    {message && <p role="status">{message}</p>}
    {!!state?.devices.length && <ul className="assistant-mobile-list">{state.devices.map(d => <li key={d.id}>
      <div><strong>{d.label}</strong><span>{d.platform === 'ios' ? 'iPhone' : 'Android'} · {d.active ? `Expires ${new Date(d.expires_at).toLocaleDateString()}` : d.status === 'revoked' ? 'Revoked' : 'Expired or incomplete'}</span></div>
      {d.status !== 'revoked' && <button className="secondary" disabled={busy} onClick={() => { void revoke(d.id); }}>Revoke</button>}
    </li>)}</ul>}
  </section>;
}
